import { computed, ref } from 'vue';
import type { ComputedRef, Ref } from 'vue';

import type { SwitchCity } from './types';
import { groupCities } from '../lib/groupCities';
import type { GroupedCities } from '../lib/groupCities';

export interface UseSwitchCity {
  currentCity: Ref<SwitchCity | null>;
  modalOpen: Ref<boolean>;
  query: Ref<string>;

  grouped: ComputedRef<GroupedCities>;
  label: ComputedRef<string>;
  hasResults: ComputedRef<boolean>;

  open: () => void;
  close: () => void;
  setQuery: (v: string) => void;
  selectCity: (city: SwitchCity) => void;
  restore: (city: SwitchCity | null) => void;
  setCities: (list: SwitchCity[]) => void;
}

export interface UseSwitchCityOptions {
  initialCities?: SwitchCity[];
}

export function useSwitchCity(options: UseSwitchCityOptions = {}): UseSwitchCity {
  const currentCity = ref<SwitchCity | null>(null);
  const modalOpen = ref(false);
  const query = ref('');
  const cities = ref<SwitchCity[]>(options.initialCities ?? []);

  const grouped = computed<GroupedCities>(() => groupCities(cities.value, query.value));

  const label = computed<string>(() => currentCity.value?.name ?? '');

  const hasResults = computed<boolean>(() => {
    const g = grouped.value;
    const groupCount = g.groups.reduce((sum, group) => sum + group.cities.length, 0);
    return g.pinned.length + groupCount > 0;
  });

  const open = (): void => {
    modalOpen.value = true;
    query.value = '';
  };

  const close = (): void => {
    modalOpen.value = false;
    query.value = '';
  };

  const setQuery = (v: string): void => {
    query.value = v;
  };

  const selectCity = (city: SwitchCity): void => {
    currentCity.value = city;
    close();
  };

  const restore = (city: SwitchCity | null): void => {
    currentCity.value = city;
  };

  const setCities = (list: SwitchCity[]): void => {
    cities.value = list;
  };

  return {
    currentCity,
    modalOpen,
    query,
    grouped,
    label,
    hasResults,
    open,
    close,
    setQuery,
    selectCity,
    restore,
    setCities,
  };
}
